// Posts to the Apps Script Web App (apps-script/Code.gs).
//
// mode: 'no-cors' — Apps Script Web App responses don't reliably carry an
// Access-Control-Allow-Origin header the browser will accept, even when
// deployed correctly (Execute as: Me, Anyone). no-cors avoids the browser
// blocking the request outright, but the tradeoff is the response comes
// back opaque: no status, no headers, no body. We can't tell success from
// failure here — AppContext re-fetches the sheet afterward and checks the
// expected row/value actually landed instead of trusting this response.
//
// Content-Type is deliberately text/plain — a JSON content type would
// trigger a CORS preflight (OPTIONS) that Apps Script doesn't handle, so the
// JSON body is sent as plain text and parsed via e.postData.contents.

// Appends a new Timelogs row — action: 'add' (Code.gs's default). This is
// what the live Check In/Out button always sends. `logtimeUtc`/`isManual`
// let Apps Script accept a backdated timestamp instead of "now" — supported
// by the backend, but not currently wired up to any UI.
export async function postCheckInOut({ appsScriptUrl, idToken, uuid, isIn, notes, logtimeUtc, isManual }) {
  const body = { idToken, uuid, action: 'add', isIn, notes };
  if (isManual) {
    body.isManual = true;
    body.logtimeUtc = logtimeUtc;
  }

  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify(body),
  });
  // A thrown error here means a real network failure (offline, DNS, etc.) —
  // no-cors only hides the response, it doesn't hide connection failures.
}

// Updates just the notes on an existing Timelogs row — action: 'edit'.
// Self-service only: Apps Script rejects this if logId doesn't belong to
// the requesting uuid. Never creates a new row.
export async function postEditNote({ appsScriptUrl, idToken, uuid, logId, notes }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'edit', logId, notes }),
  });
}

// Files a new leave request for the requesting uuid — action: 'file_leave'.
// Future dates only, checked server-side; isHalfDay/halfDayPeriod only
// apply when dateFrom equals dateTo — Code.gs requires halfDayPeriod to be
// "AM" or "PM" whenever isHalfDay is true.
export async function postFileLeave({
  appsScriptUrl,
  idToken,
  uuid,
  leaveType,
  dateFrom,
  dateTo,
  isHalfDay,
  halfDayPeriod,
  reason,
}) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({
      idToken,
      uuid,
      action: 'file_leave',
      leaveType,
      dateFrom,
      dateTo,
      isHalfDay,
      halfDayPeriod,
      reason,
    }),
  });
}

// Approves or disapproves a Pending leave — action: 'decide_leave'. Admin
// only; Apps Script rejects this otherwise. decision is 'Approved' or
// 'Disapproved'.
export async function postDecideLeave({ appsScriptUrl, idToken, uuid, leaveId, decision, decisionNotes }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'decide_leave', leaveId, decision, decisionNotes }),
  });
}

// Sets someone's LeaveBalances credits_total (their yearly maximum grant)
// and credits_remaining (live current balance) — action: 'adjust_balance'.
// Admin only. Upsert on the Apps Script side — creates their row if it
// doesn't exist yet. Both fields are always sent together — Code.gs
// rejects the whole thing if remaining would exceed total.
export async function postAdjustBalance({ appsScriptUrl, idToken, uuid, targetUuid, creditsTotal, creditsRemaining }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'adjust_balance', targetUuid, creditsTotal, creditsRemaining }),
  });
}

// Withdraws one's own still-Pending leave — action: 'cancel_leave'.
// Self-service only: Apps Script rejects this if the leave isn't theirs or
// isn't Pending anymore.
export async function postCancelLeave({ appsScriptUrl, idToken, uuid, leaveId }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'cancel_leave', leaveId }),
  });
}

// Creates a new announcement — action: 'create_announcement'. Admin only.
// `fields` carries whichever of message/recurrenceType/month/day/startDate/
// endDate/intervalDays/leadDays apply to that recurrenceType — Code.gs
// validates per-type, same rules as the create/edit form.
export async function postCreateAnnouncement({ appsScriptUrl, idToken, uuid, fields }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'create_announcement', ...fields }),
  });
}

// Overwrites an existing announcement's editable fields — action:
// 'update_announcement'. Admin only. Always sends the full field set (same
// shape as adjust_balance) — this is also how a row gets deactivated, by
// sending active: false.
export async function postUpdateAnnouncement({ appsScriptUrl, idToken, uuid, announcementId, fields, active }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'update_announcement', announcementId, ...fields, active }),
  });
}

// Permanently removes an announcement — action: 'delete_announcement'.
// Admin only.
export async function postDeleteAnnouncement({ appsScriptUrl, idToken, uuid, announcementId }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'delete_announcement', announcementId }),
  });
}

// Creates a new holiday — action: 'create_holiday'. Admin only. Month/day
// only (holidays repeat every year), no year field.
export async function postCreateHoliday({ appsScriptUrl, idToken, uuid, name, month, day }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'create_holiday', name, month, day }),
  });
}

// Overwrites an existing holiday's name/month/day — action: 'update_holiday'.
// Admin only.
export async function postUpdateHoliday({ appsScriptUrl, idToken, uuid, holidayId, name, month, day }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'update_holiday', holidayId, name, month, day }),
  });
}

// Permanently removes a holiday — action: 'delete_holiday'. Admin only.
// Code.gs also cleans up its HolidayListItems rows on its side.
export async function postDeleteHoliday({ appsScriptUrl, idToken, uuid, holidayId }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'delete_holiday', holidayId }),
  });
}

// Creates a new holiday list — action: 'create_holiday_list'. Admin only.
// holidayIds is the initial membership (can be an empty array).
export async function postCreateHolidayList({ appsScriptUrl, idToken, uuid, name, holidayIds }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'create_holiday_list', name, holidayIds }),
  });
}

// Overwrites a holiday list's name and fully replaces its membership —
// action: 'update_holiday_list'. Admin only. holidayIds is always the FULL
// desired membership (same full-state shape as adjust_balance), not a delta.
export async function postUpdateHolidayList({ appsScriptUrl, idToken, uuid, listId, name, holidayIds }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'update_holiday_list', listId, name, holidayIds }),
  });
}

// Permanently removes a holiday list — action: 'delete_holiday_list'. Admin
// only. Code.gs also cleans up its HolidayListItems rows on its side.
export async function postDeleteHolidayList({ appsScriptUrl, idToken, uuid, listId }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'delete_holiday_list', listId }),
  });
}

// Creates a new client — action: 'create_client'. Admin only.
// description/industry/contact* are all optional. contact* is the client's
// own point of contact (a named person on their side), distinct from
// ClientStaff (TUX's own staff assigned to the account).
export async function postCreateClient({
  appsScriptUrl,
  idToken,
  uuid,
  name,
  description,
  industry,
  contactName,
  contactTitle,
  contactEmail,
  contactPhone,
}) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({
      idToken,
      uuid,
      action: 'create_client',
      name,
      description,
      industry,
      contactName,
      contactTitle,
      contactEmail,
      contactPhone,
    }),
  });
}

// Overwrites an existing client's name/description/industry/contact* —
// action: 'update_client'. Admin only.
export async function postUpdateClient({
  appsScriptUrl,
  idToken,
  uuid,
  clientId,
  name,
  description,
  industry,
  contactName,
  contactTitle,
  contactEmail,
  contactPhone,
}) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({
      idToken,
      uuid,
      action: 'update_client',
      clientId,
      name,
      description,
      industry,
      contactName,
      contactTitle,
      contactEmail,
      contactPhone,
    }),
  });
}

// Permanently removes a client — action: 'delete_client'. Admin only.
// Code.gs also cleans up its ClientStaff rows on its side.
export async function postDeleteClient({ appsScriptUrl, idToken, uuid, clientId }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'delete_client', clientId }),
  });
}

// Adds (or, if already assigned, updates the role of) one staff member on a
// client — action: 'add_client_staff'. Admin only. Unlike Holiday Lists,
// staff assignments are managed one row at a time rather than a
// full-membership replace per save.
export async function postAddClientStaff({ appsScriptUrl, idToken, uuid, clientId, targetUuid, role }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'add_client_staff', clientId, targetUuid, role }),
  });
}

// Removes one staff member from a client — action: 'remove_client_staff'.
// Admin only.
export async function postRemoveClientStaff({ appsScriptUrl, idToken, uuid, clientId, targetUuid }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'remove_client_staff', clientId, targetUuid }),
  });
}

// Creates a new employee — action: 'create_employee'. Admin only. uuid is
// generated server-side (matching the existing tux-<name>-NNN convention),
// not sent here.
export async function postCreateEmployee({ appsScriptUrl, idToken, uuid, fields }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'create_employee', ...fields }),
  });
}

// Overwrites an existing employee's profile fields — action:
// 'update_employee'. Admin only. Never touches status/resignation_date —
// see postDeactivateEmployee/postReactivateEmployee for that.
export async function postUpdateEmployee({ appsScriptUrl, idToken, uuid, targetUuid, fields }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'update_employee', targetUuid, ...fields }),
  });
}

// Soft-deletes an employee — action: 'deactivate_employee'. Admin only.
// resignationDate is required by Code.gs.
export async function postDeactivateEmployee({ appsScriptUrl, idToken, uuid, targetUuid, resignationDate }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'deactivate_employee', targetUuid, resignationDate }),
  });
}

// Marks an employee Active again — action: 'reactivate_employee'. Admin only.
export async function postReactivateEmployee({ appsScriptUrl, idToken, uuid, targetUuid }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'reactivate_employee', targetUuid }),
  });
}

// Creates a new role for a client — action: 'create_role'. Admin only.
// status/job posting fields are optional; status defaults to 'Open' on the
// Code.gs side if omitted.
export async function postCreateRole({ appsScriptUrl, idToken, uuid, clientId, fields }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'create_role', clientId, ...fields }),
  });
}

// Overwrites an existing role's editable fields — action: 'update_role'.
// Admin only. clientId isn't sent — it's immutable once created.
export async function postUpdateRole({ appsScriptUrl, idToken, uuid, roleId, fields }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'update_role', roleId, ...fields }),
  });
}

// Permanently removes a role — action: 'delete_role'. Admin only. Code.gs
// also cascades its criteria/applicants/answers on its side.
export async function postDeleteRole({ appsScriptUrl, idToken, uuid, roleId }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'delete_role', roleId }),
  });
}

// Replaces a role's full criteria list — action: 'update_role_criteria'.
// Admin only. criteria is always the FULL desired list (same full-state
// shape as update_holiday_list) — each item is { criteriaId?, label,
// sortOrder }, criteriaId omitted for a brand-new question.
export async function postUpdateRoleCriteria({ appsScriptUrl, idToken, uuid, roleId, criteria }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'update_role_criteria', roleId, criteria }),
  });
}

// Creates a new applicant for a role, plus their initial answers — action:
// 'create_applicant'. Admin only. answers is { criteriaId: text }.
export async function postCreateApplicant({ appsScriptUrl, idToken, uuid, roleId, fields, answers }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'create_applicant', roleId, ...fields, answers }),
  });
}

// Overwrites an existing applicant's fields and fully replaces their
// answers — action: 'update_applicant'. Admin only. answers is always the
// FULL desired { criteriaId: text } set, same full-replace idea as
// update_role_criteria. roleId can't be changed here.
export async function postUpdateApplicant({ appsScriptUrl, idToken, uuid, applicantId, fields, answers }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'update_applicant', applicantId, ...fields, answers }),
  });
}

// Permanently removes an applicant — action: 'delete_applicant'. Admin
// only. Code.gs also removes their answers on its side.
export async function postDeleteApplicant({ appsScriptUrl, idToken, uuid, applicantId }) {
  await fetch(appsScriptUrl, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify({ idToken, uuid, action: 'delete_applicant', applicantId }),
  });
}
